// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { analyticsMessages } from "../../messages";
import { DiscountByDayPoint, DiscountedOrder } from "../../models/analytics";
import { EmptyState } from "../EmptyState";
import { surfaceCardStyle } from "./tokens";

interface DiscountAnomaliesTileProps {
  title: string;
  description?: string;
  daily: DiscountByDayPoint[];
  topOrders: DiscountedOrder[];
  formatMoney: (v: number) => string;
  onOrderClick?: (order: DiscountedOrder) => void;
  loading?: boolean;
  na?: boolean;
}

const formatShortDate = (s: string) => {
  if (!s) return "";

  const d = new Date(s + "T00:00:00Z");

  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatCustomer = (s: string) => {
  if (!s) return "Guest";

  return s;
};

export const DiscountAnomaliesTile: React.FC<DiscountAnomaliesTileProps> = ({
  title,
  description,
  daily,
  topOrders,
  formatMoney,
  onOrderClick,
  loading,
  na,
}) => {
  const intl = useIntl();
  const totalDiscount = daily.reduce((s, p) => s + p.discount, 0);
  const totalGmv = daily.reduce((s, p) => s + p.gmv, 0);
  const overallPct = totalGmv + totalDiscount > 0 ? totalDiscount / (totalGmv + totalDiscount) : 0;
  const empty = !na && !loading && daily.length === 0 && topOrders.length === 0;

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" marginBottom={4} gap={3}>
        <Box>
          <Box as="div" fontSize={2} color="default2">
            {title}
          </Box>
          {description && (
            <Box as="div" fontSize={1} color="default2" marginTop={1} style={{ opacity: 0.8 }}>
              {description}
            </Box>
          )}
        </Box>
        {!empty && (
          <Box style={{ textAlign: "right" }}>
            <Box style={{ fontSize: 11, color: "#6B7280" }}>Discount share of GMV</Box>
            <Box
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: overallPct >= 0.15 ? "#DC2626" : "#111827",
                fontFeatureSettings: '"tnum"',
              }}
            >
              {(overallPct * 100).toFixed(1)}%
            </Box>
            <Box style={{ fontSize: 11, color: "#6B7280" }}>
              {formatMoney(totalDiscount)} given on {formatMoney(totalGmv + totalDiscount)}
            </Box>
          </Box>
        )}
      </Box>

      {na ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonNA)}
        </Box>
      ) : loading ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonLoading)}
        </Box>
      ) : empty ? (
        <EmptyState message="No discounts recorded in this range" compact />
      ) : (
        <>
          {daily.length > 0 && (
            <Box style={{ height: 140 }} marginBottom={4}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15, 23, 42, 0.06)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    tickLine={false}
                    axisLine={{ stroke: "rgba(15, 23, 42, 0.08)" }}
                    tickFormatter={formatShortDate}
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                    width={36}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "none",
                      background: "rgba(17, 24, 39, 0.95)",
                      color: "#fff",
                      fontSize: 11,
                    }}
                    labelFormatter={(d: string) => formatShortDate(d)}
                    formatter={(v: number) => `${(v * 100).toFixed(1)}%`}
                  />
                  <Bar dataKey="pct" fill="#F59E0B" radius={[3, 3, 0, 0]} name="Discount %" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {topOrders.length > 0 && (
            <Box>
              <Box as="div" fontSize={1} color="default2" marginBottom={2} style={{ fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>
                Deepest discounts (top 5 orders)
              </Box>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
                <thead>
                  <tr style={{ color: "#6B7280" }}>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Order
                    </th>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Customer
                    </th>
                    <th style={{ textAlign: "right", padding: "6px 8px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Total
                    </th>
                    <th style={{ textAlign: "right", padding: "6px 8px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Discount
                    </th>
                    <th style={{ textAlign: "right", padding: "6px 8px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topOrders.map((o, i) => {
                    const bottom = i === topOrders.length - 1 ? "none" : "1px solid rgba(15, 23, 42, 0.06)";
                    const sev = o.pct >= 0.5 ? "#DC2626" : o.pct >= 0.25 ? "#F59E0B" : "#6B7280";

                    return (
                      <tr
                        key={o.id}
                        onClick={onOrderClick ? () => onOrderClick(o) : undefined}
                        style={{ cursor: onOrderClick ? "pointer" : "default", transition: "background 120ms ease" }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLTableRowElement).style.background = "rgba(15, 23, 42, 0.025)";
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLTableRowElement).style.background = "transparent";
                        }}
                      >
                        <td style={{ padding: "8px", borderBottom: bottom, fontWeight: 600, color: "#111827" }}>
                          #{o.number || o.id.slice(-6)}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: bottom,
                            color: "#374151",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 220,
                          }}
                        >
                          {formatCustomer(o.customer)}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right", borderBottom: bottom, color: "#374151", fontFeatureSettings: '"tnum"' }}>
                          {formatMoney(o.total)}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right", borderBottom: bottom, color: "#374151", fontFeatureSettings: '"tnum"' }}>
                          {formatMoney(o.discount)}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            textAlign: "right",
                            borderBottom: bottom,
                            color: sev,
                            fontWeight: 700,
                            fontFeatureSettings: '"tnum"',
                          }}
                        >
                          {(o.pct * 100).toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
